/**
 * smc/structure.ts
 * Swing point detection and BOS/CHOCH structure event detection.
 */
import { Candle, StructureEvent } from '../../../../types';
import { SwingPoint, SMCThresholds } from './types';

export function detectSwingHighs(candles: Candle[], lookback: number): SwingPoint[] {
  const pts: SwingPoint[] = [];
  for (let i = lookback; i < candles.length - lookback; i++) {
    const p = candles[i].high;
    const leftOk  = candles.slice(i - lookback, i).every(c => c.high < p);
    const rightOk = candles.slice(i + 1, i + lookback + 1).every(c => c.high < p);
    if (leftOk && rightOk) pts.push({ price: p, idx: i, ts: candles[i].ts });
  }
  return pts;
}

export function detectSwingLows(candles: Candle[], lookback: number): SwingPoint[] {
  const pts: SwingPoint[] = [];
  for (let i = lookback; i < candles.length - lookback; i++) {
    const p = candles[i].low;
    const leftOk  = candles.slice(i - lookback, i).every(c => c.low > p);
    const rightOk = candles.slice(i + 1, i + lookback + 1).every(c => c.low > p);
    if (leftOk && rightOk) pts.push({ price: p, idx: i, ts: candles[i].ts });
  }
  return pts;
}

/**
 * BOS: break of the last structural HH (bullish) or LL (bearish) — continuation.
 * CHOCH: break of the last structural HL (bearish flip) or LH (bullish flip) — reversal.
 *
 * Confirmation: candle close beyond the level, OR wick break + range expansion
 * (displacement proxy). No global trend dependency — purely swing-sequence driven.
 *
 * structureConfidence (0–1) per event:
 *   - swing clarity:   are the HH/HL/LH/LL well-separated? (+0.3 if >3 swing pts)
 *   - confirmation:    close-based vs wick+expansion (+0.3 close, +0.15 wick+disp)
 *   - sequence count:  how many confirming swings back this? (up to +0.4)
 */
export function detectStructureEvents(
  candles: Candle[],
  swingHighs: SwingPoint[],
  swingLows: SwingPoint[],
  thresholds: SMCThresholds
): StructureEvent[] {
  const events: StructureEvent[] = [];
  if (swingHighs.length < 2 || swingLows.length < 2) return events;

  const recentHighs = swingHighs.slice(-5);
  const recentLows  = swingLows.slice(-5);

  const lastHH = recentHighs.length >= 2 ? recentHighs[recentHighs.length - 1] : null;
  const prevHH = recentHighs.length >= 2 ? recentHighs[recentHighs.length - 2] : null;
  const lastLL = recentLows.length >= 2  ? recentLows[recentLows.length - 1]  : null;
  const prevLL = recentLows.length >= 2  ? recentLows[recentLows.length - 2]  : null;

  const isHH = !!(lastHH && prevHH && lastHH.price > prevHH.price);
  const isLL = !!(lastLL && prevLL && lastLL.price < prevLL.price);
  const isHL = recentLows.length >= 2 && recentLows[recentLows.length-1].price > recentLows[recentLows.length-2].price;
  const isLH = recentHighs.length >= 2 && recentHighs[recentHighs.length-1].price < recentHighs[recentHighs.length-2].price;

  const bullishBiasScore = (isHH ? 1.0 : 0) + (isHL ? 0.8 : 0);
  const bearishBiasScore = (isLL ? 1.0 : 0) + (isLH ? 0.8 : 0);

  const FULL_SEQ_THRESH    = 1.8;
  const PARTIAL_SEQ_THRESH = 0.8;

  const bullishSequence   = bullishBiasScore >= FULL_SEQ_THRESH;
  const bearishSequence   = bearishBiasScore >= FULL_SEQ_THRESH;
  const bullishTransition = bullishBiasScore >= PARTIAL_SEQ_THRESH;
  const bearishTransition = bearishBiasScore >= PARTIAL_SEQ_THRESH;

  const swingClarity = Math.min(0.3, (recentHighs.length + recentLows.length) * 0.025);
  const seqScore = Math.min(0.4,
    (bullishBiasScore / 1.8) * 0.2 + (bearishBiasScore / 1.8) * 0.2
  );

  const last10Bodies = candles.slice(-10).map(c => Math.abs(c.close - c.open));
  const avgBody = last10Bodies.length ? last10Bodies.reduce((a, b) => a + b, 0) / last10Bodies.length : 0;

  const scanStart = Math.max(0, candles.length - 50);

  for (let i = scanStart + 1; i < candles.length; i++) {
    const c    = candles[i];
    const prev = candles[i - 1];

    // BOS bullish
    if (lastHH && lastHH.idx < i && bullishSequence) {
      const closeBreak = c.close > lastHH.price;
      const wickBreak  = c.high  > lastHH.price && !closeBreak;
      const rangeExpansion = avgBody > 0 && Math.abs(c.close - c.open) > avgBody * 1.2;
      const confirmed = closeBreak || (wickBreak && rangeExpansion);
      if (confirmed && prev.close <= lastHH.price) {
        const confScore = Math.min(1, swingClarity + (closeBreak ? 0.3 : 0.15) + Math.min(0.4, seqScore));
        events.push({
          type: 'BOS', dir: 'bullish', price: lastHH.price, candleIdx: i, ts: c.ts,
          strength: confScore >= 0.6 ? 'major' : 'minor',
          description: `Bullish BOS: close above HH ${lastHH.price.toFixed(2)} — continuation (confidence ${(confScore*100).toFixed(0)}%)`,
        });
      }
    }

    // BOS bearish
    if (lastLL && lastLL.idx < i && bearishSequence) {
      const closeBreak = c.close < lastLL.price;
      const wickBreak  = c.low   < lastLL.price && !closeBreak;
      const rangeExpansion = avgBody > 0 && Math.abs(c.close - c.open) > avgBody * 1.2;
      const confirmed = closeBreak || (wickBreak && rangeExpansion);
      if (confirmed && prev.close >= lastLL.price) {
        const confScore = Math.min(1, swingClarity + (closeBreak ? 0.3 : 0.15) + Math.min(0.4, seqScore));
        events.push({
          type: 'BOS', dir: 'bearish', price: lastLL.price, candleIdx: i, ts: c.ts,
          strength: confScore >= 0.6 ? 'major' : 'minor',
          description: `Bearish BOS: close below LL ${lastLL.price.toFixed(2)} — continuation (confidence ${(confScore*100).toFixed(0)}%)`,
        });
      }
    }

    // CHOCH bullish
    if (isLH && recentHighs.length >= 2 && recentHighs[recentHighs.length-1].idx < i && !bullishSequence && bearishTransition) {
      const lhLevel = recentHighs[recentHighs.length - 1];
      const closeBreak = c.close > lhLevel.price;
      const wickBreak  = c.high  > lhLevel.price && !closeBreak;
      const rangeExpansion = avgBody > 0 && Math.abs(c.close - c.open) > avgBody * 1.2;
      const confirmed = closeBreak || (wickBreak && rangeExpansion);
      if (confirmed && prev.close <= lhLevel.price) {
        const confScore = Math.min(1, swingClarity + (closeBreak ? 0.3 : 0.15) + Math.min(0.3, seqScore));
        events.push({
          type: 'CHOCH', dir: 'bullish', price: lhLevel.price, candleIdx: i, ts: c.ts,
          strength: confScore >= 0.55 ? 'major' : 'minor',
          description: `Bullish CHOCH: break above LH ${lhLevel.price.toFixed(2)} — structure shift candidate (confidence ${(confScore*100).toFixed(0)}%)`,
        });
      }
    }

    // CHOCH bearish
    if (isHL && recentLows.length >= 2 && recentLows[recentLows.length-1].idx < i && !bearishSequence && bullishTransition) {
      const hlLevel = recentLows[recentLows.length - 1];
      const closeBreak = c.close < hlLevel.price;
      const wickBreak  = c.low   < hlLevel.price && !closeBreak;
      const rangeExpansion = avgBody > 0 && Math.abs(c.close - c.open) > avgBody * 1.2;
      const confirmed = closeBreak || (wickBreak && rangeExpansion);
      if (confirmed && prev.close >= hlLevel.price) {
        const confScore = Math.min(1, swingClarity + (closeBreak ? 0.3 : 0.15) + Math.min(0.3, seqScore));
        events.push({
          type: 'CHOCH', dir: 'bearish', price: hlLevel.price, candleIdx: i, ts: c.ts,
          strength: confScore >= 0.55 ? 'major' : 'minor',
          description: `Bearish CHOCH: break below HL ${hlLevel.price.toFixed(2)} — structure shift candidate (confidence ${(confScore*100).toFixed(0)}%)`,
        });
      }
    }
  }

  events.sort((a, b) => b.candleIdx - a.candleIdx);
  const deduped: StructureEvent[] = [];
  for (const e of events) {
    const tooClose = deduped.some(d => Math.abs(d.price - e.price) / e.price < 0.003 && d.type === e.type);
    if (!tooClose) deduped.push(e);
  }
  return deduped.slice(0, thresholds.maxStructureEvents);
}
